import { addMonths, isBefore } from "date-fns";
import { z } from "zod";
import { ContentGroup } from "../../../core/resource/types";
import { formatMessage } from "../../../utils";
import {
  isAlphaNumeric,
  isEmail,
  isName,
  isPhone,
} from "../../../utils/validation";
import { FlightPassengerDocumentType } from "./types";

function ageOn(dob: Date, onDate: Date) {
  let age = onDate.getFullYear() - dob.getFullYear();
  const m = onDate.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && onDate.getDate() < dob.getDate())) age--;
  return age;
}

const createDocumentDetailSchema = (cr: ContentGroup, departureDate: Date) =>
  z
    .object({
      documentNo: z.string(),
      documentType: z.nativeEnum(FlightPassengerDocumentType),
      issuingCountry: z.string().nullable().optional(),
      expirationDate: z
        .date()
        .nullable()
        .optional()
        .refine(
          (val) => {
            if (!val) return true;
            const minValid = addMonths(departureDate, 6);
            return !isBefore(val, minValid);
          },
          { message: cr.documentExpiryLessThan6Months }
        ),
    })
    .superRefine((doc, ctx) => {
      const no = doc.documentNo;
      if (no === null) return;

      if (
        no.length === 0 &&
        doc.documentType === FlightPassengerDocumentType.PASSPORT
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["documentNo"],
          message: cr.internationalDocumentNumberRequired,
        });
      } else if (!isAlphaNumeric(no)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["documentNo"],
          message: cr.alphabetNumericError,
        });
      }
      if (doc.documentType === FlightPassengerDocumentType.PASSPORT) {
        if (!doc.expirationDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["expirationDate"],
            message: cr.internationalExpirationDateRequired,
          });
        }

        if (!doc.issuingCountry) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["issuingCountry"],
            message: cr.internationalIssuingCountryRequired,
          });
        }

        if (no.length < 6 || no.length > 9) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["documentNo"],
            message: cr.internationalDocumentNumberNotValid,
          });
        }
      }
    });

const dateOfBirthField = (cr: ContentGroup) =>
  z
    .date()
    .optional()
    .nullable()
    .refine(
      (val) => {
        if (val === undefined) return true; // field not rendered → valid
        if (val === null) return false; // rendered but empty → invalid
        return !Number.isNaN(new Date(val).getTime());
      },
      { message: cr.passengerBirthDateRequired }
    );

const passengerBaseField = (cr: ContentGroup, departureDate: Date) =>
  z.object({
    title: z.string().min(1, cr.passengerFormTitleFieldRequired),
    gender: z.literal("M").or(z.literal("F")),
    fullname: z
      .string()
      .min(1, cr.passengerFormFullnameFieldRequired)
      .refine(
        (v) => {
          if (!isName(v)) return false;
          return true;
        },
        { message: cr.alphabetError }
      ),
    nationality: z
      .string({ message: cr.nationalityRequiredErrorMessage })
      .min(1, cr.nationalityRequiredErrorMessage),
    documentDetail: createDocumentDetailSchema(cr, departureDate).nullable(),
  });

function withInternationalDocRules<T extends z.ZodTypeAny>(
  schema: T,
  cr: ContentGroup,
  isTripDomesticID: boolean
) {
  return schema.superRefine((p: any, ctx) => {
    const nationality: string | null = p.nationality ?? null;
    const domesticLike =
      isTripDomesticID && (!nationality || nationality === "ID");
    const hasDoc = p.documentDetail !== undefined;

    // if a document exists & the trip is NOT domestic-like → issuingCountry & expirationDate fields are required
    if (hasDoc && !domesticLike) {
      if (!p.documentDetail.issuingCountry) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["documentDetail", "issuingCountry"],
          message: cr.internationalIssuingCountryRequired,
        });
      }
      if (!p.documentDetail.expirationDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["documentDetail", "expirationDate"],
          message: cr.internationalExpirationDateRequired,
        });
      }
    }
  });
}

export function createAdultPassengerSchema(
  cr: ContentGroup,
  departureDate: Date,
  isTripDomesticID: boolean
) {
  return withInternationalDocRules(
    z
      .object({
        type: z.literal("ADULT"),
        email: z
          .string()
          .min(1, cr.passengerFormEmailFieldRequired)
          .refine(
            (v) => {
              if (v === undefined) return true;
              if (!isEmail(v)) return false;
              return true;
            },
            { message: cr.invalidEmailFormat }
          )
          .optional(),
        isEmployee: z.boolean(),
        addOns: z.unknown(),
      })
      .merge(passengerBaseField(cr, departureDate))
      .extend({
        dateOfBirth: dateOfBirthField(cr),
      })
      .superRefine((p, ctx) => {
        const dob = p.dateOfBirth;
        if (dob === undefined || dob === null) return;
        if (Number.isNaN(dob.getTime())) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: cr.passengerBirthDateRequired,
            path: ["dateOfBirth"],
          });
          return;
        }
        const a = ageOn(dob, departureDate);
        if (a < 12 || a > 112) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: cr.dateOfBirthAgeRestriction, // ≥12 and ≤112
            path: ["dateOfBirth"],
          });
        }
      }),
    cr,
    isTripDomesticID
  );
}

export function createChildPassengerSchema(
  cr: ContentGroup,
  departureDate: Date,
  isTripDomesticID: boolean
) {
  return withInternationalDocRules(
    z
      .object({
        type: z.literal("CHILD"),
        addOns: z.unknown(),
      })
      .merge(passengerBaseField(cr, departureDate))
      .extend({
        dateOfBirth: dateOfBirthField(cr),
      })
      .superRefine((p, ctx) => {
        const dob = p.dateOfBirth;
        if (dob === undefined || dob === null) return;
        if (Number.isNaN(dob.getTime())) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: cr.passengerBirthDateRequired,
            path: ["dateOfBirth"],
          });
          return;
        }
        const a = ageOn(dob, departureDate);
        if (a < 2 || a > 11) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: cr.dateOfBirthChildAgeRestriction, // 2..11
            path: ["dateOfBirth"],
          });
        }
      }),
    cr,
    isTripDomesticID
  );
}

export function createInfantPassengerSchema(
  cr: ContentGroup,
  departureDate: Date,
  isTripDomesticID: boolean
) {
  return withInternationalDocRules(
    z
      .object({
        type: z.literal("INFANT"),
      })
      .merge(passengerBaseField(cr, departureDate))
      .extend({
        dateOfBirth: dateOfBirthField(cr),
      })
      .superRefine((p, ctx) => {
        const dob = p.dateOfBirth;
        if (dob === undefined || dob === null) return;
        if (Number.isNaN(dob.getTime())) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: cr.passengerBirthDateRequired,
            path: ["dateOfBirth"],
          });
          return;
        }
        const a = ageOn(dob, departureDate);
        if (a >= 2) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: cr.dateOfBirthInfantAgeRestriction, // <2
            path: ["dateOfBirth"],
          });
        }
      }),
    cr,
    isTripDomesticID
  );
}

const contactPhoneField = (cr: ContentGroup) => {
  const phoneNumberRequired = formatMessage(cr.phoneMinimumLengthError, {
    num: 7,
  });
  return z
    .object({
      prefix: z.string().min(1, phoneNumberRequired),
      phone: z
        .string()
        .min(7, phoneNumberRequired)
        .max(15, {
          message: formatMessage(cr.phoneMaximumLengthError, { num: 15 }),
        })
        .refine((val) => isPhone(val), {
          message: cr.phoneNumericError,
        }),
    })
    .nullable();
};

export function createFlightPrebookFormSchemaValidation(
  cr: ContentGroup,
  departureDate: Date,
  isTripDomesticID: boolean
) {
  return z.object({
    contact: z.object({
      fullname: z.string(),
      email: z.string(),
      phone: contactPhoneField(cr),
    }),
    passengers: z.object({
      adults: z.array(
        createAdultPassengerSchema(cr, departureDate, isTripDomesticID)
      ),
      children: z.array(
        createChildPassengerSchema(cr, departureDate, isTripDomesticID)
      ),
      infants: z.array(
        createInfantPassengerSchema(cr, departureDate, isTripDomesticID)
      ),
    }),
    notes: z
      .string()
      .min(1, { message: cr.notesRequiredErrorMessage })
      .optional()
      .refine(
        (v) => {
          if (v === undefined) return true;
          if (v.includes("<") || v.includes(">")) return false;
          return true;
        },
        { message: cr.notesFormatMessageError }
      ),
    attachment: z
      .object({ uri: z.string(), name: z.string(), type: z.string() })
      .optional(),
    flight: z.unknown(),
    additionalFees: z.unknown().optional(),
    grandTotal: z.unknown().optional(),
    totalFareFromClient: z.unknown().optional(),
    searchId: z.unknown().optional(),
    addOnsBookingFormId: z.unknown().optional(),
    policyBypassRemarks: z.string().optional(),
    crossSellProductAddOns: z.unknown().optional(),
  });
}
